import { createAsyncThunk, createSlice, isRejectedWithValue, PayloadAction } from "@reduxjs/toolkit";
import api from "../api";
import { RootState } from "../index";
import { asArray, isRecord } from "../payloadGuards";

interface ResourceState {
    practices: any[];
    modalities: any[];
    insurances: any[];
    specialties: any[];
    sessionTypes: any[];
    ages: any[];
    languages: any[];
    isLoading: boolean;
    states: any[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: ResourceState = {
    practices: [],
    modalities: [],
    languages: [],
    sessionTypes: [],
    insurances: [],
    specialties: [],
    ages: [], // Fixed: Added missing ages array
    isLoading: false,
    states: [],
    status: 'idle',
    error: null,
};

const handleApiError = (error: unknown) => {
    const err = error as Error;
    return err.message || 'An error occurred';
};

// Existing fetch actions
export const fetchPractices = createAsyncThunk(
    'resources/fetchPractices',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/practice');
            return response.data.body;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const fetchModalities = createAsyncThunk(
    'resources/fetchModalities',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/modality');

            let data: any;
            if (response.data?.body && typeof response.data.body === 'string') {
                try {
                    data = JSON.parse(response.data.body);
                } catch (e) {
                    console.error('Error parsing modalities body:', e);
                    data = response.data.body;
                }
            } else {
                data = response.data.body || response.data;
            }

            return data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const fetchInsurances = createAsyncThunk(
    'resources/fetchInsurances',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/insurance');

            // Handle the response data structure properly
            let insuranceData;
            if (typeof response.data.body === 'string') {
                insuranceData = JSON.parse(response.data.body);
            } else {
                insuranceData = response.data.body;
            }

            return insuranceData;
        } catch (error) {
            console.error('Error fetching insurances:', error);
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const fetchSpecialties = createAsyncThunk(
    'resources/fetchSpecialties',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/specialty');

            let data: any;
            if (response.data?.body && typeof response.data.body === 'string') {
                try {
                    data = JSON.parse(response.data.body);
                } catch (e) {
                    console.error('Error parsing specialties body:', e);
                    data = response.data.body;
                }
            } else {
                data = response.data.body || response.data;
            }

            return data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const fetchStates = createAsyncThunk(
    'resources/fetchStates',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/states');
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

// NEW: Age Range actions
export const fetchAgeRange = createAsyncThunk(
    'resources/fetchAgeRange',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/age');

            let data: any;
            if (response.data?.body && typeof response.data.body === 'string') {
                try {
                    data = JSON.parse(response.data.body);
                } catch (e) {
                    console.error('Error parsing age range body:', e);
                    data = response.data.body;
                }
            } else {
                data = response.data.body || response.data;
            }

            return data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const addAgeRange = createAsyncThunk(
    'resources/addAgeRange',
    async (ageRangeData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/age', ageRangeData);
            return response.data.body || response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const updateAgeRange = createAsyncThunk(
    'resources/updateAgeRange',
    async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/age/${id}`, data);
            return response.data.body || response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const deleteAgeRange = createAsyncThunk(
    'resources/deleteAgeRange',
    async (ageRangeId: string, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/age-range/${ageRangeId}`);
            return { id: ageRangeId, ...response.data };
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

// Practice CRUD
export const createPractice = createAsyncThunk(
    'resources/createPractice',
    async (practice: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/practices', practice);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const updatePractice = createAsyncThunk(
    'resources/updatePractice',
    async (practice: any, { rejectWithValue }) => {
        try {
            const response = await api.put(`/practices/${practice.id}`, practice);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const deletePractice = createAsyncThunk(
    'resources/deletePractice',
    async (practiceId: string, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/practices/${practiceId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

// Modality CRUD - Fixed create action
export const createModalities = createAsyncThunk(
    'resources/createModalities',
    async (modalityData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/modalities', modalityData);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const updateModalities = createAsyncThunk(
    'resources/updateModalities',
    async (modality: any, { rejectWithValue }) => {
        try {
            const response = await api.put(`/modalities/${modality.id}`, modality);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const deleteModalities = createAsyncThunk(
    'resources/deleteModalities',
    async (modalityId: string, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/modalities/${modalityId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

// Insurance CRUD - Fixed create action
export const createInsurances = createAsyncThunk(
    'resources/createInsurances',
    async (insuranceData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/insurances', insuranceData);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const updateInsurances = createAsyncThunk(
    'resources/updateInsurances',
    async (insurance: any, { rejectWithValue }) => {
        try {
            const response = await api.put(`/insurances/${insurance.id}`, insurance);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const deleteInsurances = createAsyncThunk(
    'resources/deleteInsurances',
    async (insuranceId: string, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/insurances/${insuranceId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

// Specialty CRUD - Fixed create action
export const createSpecialties = createAsyncThunk(
    'resources/createSpecialties',
    async (specialtyData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/specialties', specialtyData);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const updateSpecialties = createAsyncThunk(
    'resources/updateSpecialties',
    async (specialty: any, { rejectWithValue }) => {
        try {
            const response = await api.put(`/specialties/${specialty.id}`, specialty);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const deleteSpecialties = createAsyncThunk(
    'resources/deleteSpecialties',
    async (specialtyId: string, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/specialties/${specialtyId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

// States CRUD - Fixed create and update actions
export const createStates = createAsyncThunk(
    'resources/createStates',
    async (stateData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/states', stateData);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const updateStates = createAsyncThunk(
    'resources/updateStates',
    async (state: any, { rejectWithValue }) => {
        try {
            const response = await api.put(`/states/${state.id}`, state);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const deleteStates = createAsyncThunk(
    'resources/deleteStates',
    async (stateId: string, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/states/${stateId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const fetchSessionTypes = createAsyncThunk(
    'resources/fetchSessionTypes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`/sessiontypes`);

            let data: any;
            if (response.data?.body && typeof response.data.body === 'string') {
                try {
                    data = JSON.parse(response.data.body);
                } catch (e) {
                    console.error('Error parsing session types body:', e);
                    data = response.data.body;
                }
            } else {
                data = response.data.body || response.data;
            }

            return data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
)

export const fetchLanguages = createAsyncThunk(
    'resources/fetchLanguages',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`/languages`);

            let data: any;
            if (response.data?.body && typeof response.data.body === 'string') {
                try {
                    data = JSON.parse(response.data.body);
                } catch (e) {
                    console.error('Error parsing languages body:', e);
                    data = response.data.body;
                }
            } else {
                data = response.data.body || response.data;
            }

            return data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
)

// Session Types CRUD
export const createSessionType = createAsyncThunk(
    'resources/createSessionType',
    async (sessionTypeData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/sessiontypes', sessionTypeData);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const updateSessionType = createAsyncThunk(
    'resources/updateSessionType',
    async (sessionType: any, { rejectWithValue }) => {
        try {
            const response = await api.put(`/sessiontypes/${sessionType.id}`, sessionType);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

export const deleteSessionType = createAsyncThunk(
    'resources/deleteSessionType',
    async (sessionTypeId: string, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/sessiontypes/${sessionTypeId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleApiError(error));
        }
    }
);

const resourcesSlice = createSlice({
    name: 'resources',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Practices
            .addCase(fetchPractices.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchPractices.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.practices = asArray(action.payload);
                state.isLoading = false;
            })
            .addCase(fetchPractices.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })

            // Modalities
            .addCase(fetchModalities.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchModalities.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.modalities = asArray(action.payload);
                state.isLoading = false;
            })
            .addCase(fetchModalities.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })

            // Insurances
            .addCase(fetchInsurances.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchInsurances.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.insurances = asArray(action.payload);
            })
            .addCase(fetchInsurances.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })

            // Specialties
            .addCase(fetchSpecialties.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchSpecialties.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.specialties = asArray(action.payload);
            })
            .addCase(fetchSpecialties.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })

            // States
            .addCase(fetchStates.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchStates.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.states = asArray(action.payload);
            })
            .addCase(fetchStates.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })

            // NEW: Age Ranges
            .addCase(fetchAgeRange.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchAgeRange.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.ages = asArray(action.payload);
                state.isLoading = false;
            })
            .addCase(fetchAgeRange.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })

            .addCase(addAgeRange.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(addAgeRange.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                if (isRecord(action.payload)) state.ages.push(action.payload);
                state.isLoading = false;
            })
            .addCase(addAgeRange.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })

            .addCase(updateAgeRange.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(updateAgeRange.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                const index = state.ages.findIndex(age => age.id === action.payload.id);
                if (index !== -1) {
                    state.ages[index] = action.payload;
                }
                state.isLoading = false;
            })
            .addCase(updateAgeRange.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })

            .addCase(deleteAgeRange.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(deleteAgeRange.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                state.ages = state.ages.filter(age => age.id !== action.payload.id);
                state.isLoading = false;
            })
            .addCase(deleteAgeRange.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })

            // Session Types
            .addCase(fetchSessionTypes.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchSessionTypes.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.sessionTypes = asArray(action.payload);
                state.isLoading = false;
            })
            .addCase(fetchSessionTypes.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })
            .addCase(createSessionType.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(createSessionType.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                if (isRecord(action.payload)) state.sessionTypes.push(action.payload);
                state.isLoading = false;
            })
            .addCase(createSessionType.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })
            .addCase(updateSessionType.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(updateSessionType.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                const index = state.sessionTypes.findIndex(st => st.id === action.payload.id);
                if (index !== -1) {
                    state.sessionTypes[index] = action.payload;
                }
                state.isLoading = false;
            })
            .addCase(updateSessionType.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })
            .addCase(deleteSessionType.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(deleteSessionType.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                state.sessionTypes = state.sessionTypes.filter(st => st.id !== action.payload.id);
                state.isLoading = false;
            })
            .addCase(deleteSessionType.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.isLoading = false;
            })

            // Languages
            .addCase(fetchLanguages.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchLanguages.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.status = 'succeeded';
                state.languages = asArray(action.payload);
                state.isLoading = false;
            })
            .addCase(fetchLanguages.rejected, (state) => {
                state.status = 'failed';
                state.error = 'Failed to fetch languages';
                state.isLoading = false;
            });
    },
});

export default resourcesSlice.reducer;
